import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Download, Search, Filter
} from "lucide-react";
import { useGetList } from "ra-core";
import type { CustomerInvoice, Company } from "../types";

interface InvoiceRow extends CustomerInvoice {
  customer?: Company;
}

function formatCurrency(amount: number | null | undefined): string {
  if (amount == null) return "₹0.00";
  return `₹${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getPaymentStatus(invoice: CustomerInvoice): "Paid" | "Pending" | "Overdue" {
  if (invoice.status === "paid") return "Paid";
  if (invoice.paid_amount >= invoice.total_amount) return "Paid";
  if (invoice.due_date && new Date(invoice.due_date) < new Date()) return "Overdue";
  return "Pending";
}

export function SalesList() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: invoices, isLoading, error } = useGetList<InvoiceRow>("customer_invoices", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "date", order: "DESC" },
  });

  const filteredInvoices = invoices?.filter(inv => 
    searchTerm === "" || 
    inv.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleAll = () => {
    if (!filteredInvoices) return;
    setSelected(selected.length === filteredInvoices.length ? [] : filteredInvoices.map(d => String(d.id)));
  };

  const toggleOne = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-rose-600 font-medium">Failed to load sales ledger: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 min-h-screen bg-slate-50/30">
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b pb-5 gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900">Global Sales Ledger</h1>
          <p className="text-slate-500 text-sm font-medium mt-1.5">
            {filteredInvoices?.length ?? 0} invoice(s) from database
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" className="h-9 shadow-sm border-slate-300 font-bold text-slate-600 bg-white">
            <Download className="w-4 h-4 mr-2" /> Export Data
          </Button>
        </div>
      </div>

      <Card className="shadow-none border border-slate-200 bg-white overflow-hidden">
        <CardHeader className="border-b bg-slate-50/50 flex flex-row items-center justify-between py-4">
          <CardTitle className="text-base font-bold text-slate-900">Transaction History</CardTitle>
          <div className="flex items-center gap-3">
             {selected.length > 0 && (
               <Badge className="bg-primary text-white shadow-sm px-3 py-1 animate-in slide-in-from-right-2">{selected.length} Selected</Badge>
             )}
             <div className="relative max-w-xs hidden md:block">
               <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
               <Input 
                 placeholder="Search invoices..." 
                 value={searchTerm}
                 onChange={(e) => setSearchTerm(e.target.value)}
                 className="h-8 pl-8 bg-white border-slate-300 text-xs w-[200px] shadow-none" 
               />
             </div>
             <Button variant="outline" size="sm" className="h-8 border-slate-300 text-xs font-bold text-slate-600 hover:bg-slate-100">
               <Filter className="w-3.5 h-3.5 mr-1.5" /> Filters
             </Button>
          </div>
        </CardHeader>

        <div className="w-full overflow-x-auto flex-1 border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-slate-50 border-b-2 border-slate-200">
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200 w-10">
                  <Checkbox 
                    checked={filteredInvoices && selected.length === filteredInvoices.length && filteredInvoices.length > 0} 
                    onCheckedChange={toggleAll} 
                  />
                </th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">Invoice Reference</th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">Customer</th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200 text-right">Total Amount</th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">Invoice Date</th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 border-r border-slate-200">Due Date</th>
                <th className="px-4 py-3.5 text-xs font-bold uppercase tracking-wider text-slate-600 text-right">Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices?.map((invoice) => {
                const status = getPaymentStatus(invoice);
                return (
                  <tr 
                    key={invoice.id} 
                    className={`${selected.includes(String(invoice.id)) ? 'bg-blue-50/40' : 'even:bg-slate-50/50'} hover:bg-slate-100/50 border-b border-slate-200 transition-colors`}
                  >
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 align-middle">
                      <Checkbox 
                        checked={selected.includes(String(invoice.id))} 
                        onCheckedChange={() => toggleOne(String(invoice.id))} 
                      />
                    </td>
                    <td
                      className="px-4 py-3.5 text-sm border-r border-slate-200 font-mono font-bold text-primary hover:underline cursor-pointer align-middle"
                      onClick={() => navigate(`/customer_invoices/${invoice.id}/show`)}
                    >
                      {invoice.reference}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 font-bold text-slate-900 align-middle">
                      {invoice.customer?.name || 'Unknown'}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 text-right font-black text-slate-900 align-middle">
                      {formatCurrency(invoice.total_amount)}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 text-slate-600 font-medium align-middle">
                      {invoice.date || '-'}
                    </td>
                    <td className="px-4 py-3.5 text-sm border-r border-slate-200 text-slate-600 font-medium align-middle">
                      {invoice.due_date || '-'}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-right align-middle">
                      <Badge className={`
                        ${status === "Paid" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : ""}
                        ${status === "Pending" ? "bg-amber-100 text-amber-800 border-amber-200" : ""}
                        ${status === "Overdue" ? "bg-rose-100 text-rose-800 border-rose-200" : ""}
                        font-bold border shadow-none shadow-sm
                      `}>
                        {status}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(!filteredInvoices || filteredInvoices.length === 0) && (
            <div className="p-8 text-center text-slate-500">
              No invoices found. Create invoices from the Invoices module.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
